import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, formatUsd, formatUptime, formatRelative } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Card, StatTile, StatusPill, Button, Empty, Spinner } from '../components/ui';

/**
 * The page open during a show: everything live, what needs attention, who
 * is on, the latest recordings, what people have just done, and the hub's
 * own health. One request (GET /api/overview, services/overview.ts), every
 * 20 s. docs/tools/overview.md.
 */

interface Attention { level: 'warn' | 'bad'; text: string; to: string | null }
interface LiveMachine { id: string; name: string; state: string; type: string | null; region: string; groupId: string | null; since: string | null; usdPerHour: number | null; sessionUsd: number | null; pending: string | null; adopted: boolean }
interface LiveEncoder { id: string; name: string; state: string; region: string; channelState: string | null; activeInput: string | null; alerts: { type: string; message: string }[]; stopAt: string | null; usdPerHour: number | null; busy: boolean; error: string | null; progress: string | null }
interface LiveFlow { id: string; name: string; state: string; stopAt: string | null; usdPerHour: number | null; encoderId: string | null; error: string | null; busy: boolean }
interface LiveRecorder { id: string; ownerKind: 'encoder' | 'flow'; ownerId: string; name: string; state: string; startedAt: string | null; stopAt: string | null; startAt: string | null; error: string | null }
interface Transcript { state: 'none' | 'working' | 'ready'; percent: number; lines: number }
interface Recent { instanceId: string; session: string; prefix: string; title: string; seconds: number; recording: boolean; poster: string | null; transcript: Transcript }
interface Activity { at: string; userEmail: string | null; action: string; targetType: string | null; targetId: string | null; detail: Record<string, unknown>; result: string }

interface Overview {
  at: string;
  live: { machines: LiveMachine[]; encoders: LiveEncoder[]; flows: LiveFlow[]; recorders: LiveRecorder[] };
  costs: { usdPerHourNow: number; todayUsd: number; monthUsd: number; monthToDate: number | null; forecast: number | null; costExplorerError: string | null; budgets: { id: string; name: string; period: string; thresholdUsd: number; spentUsd: number }[] } | null;
  attention: Attention[];
  people: { devicesOnline: number; devicesTotal: number; intercom: { online: number; channels: number; users: number } | null; call: { sessionsOpen: number; seatsLive: number; hostsLive: number } | null };
  recordings: { recent: Recent[]; transcribing: number; joining: number } | null;
  activity: Activity[] | null;
  hub: { disk: { freeBytes: number; totalBytes: number } | null; monitor: { running: boolean; lastRunAt: string | null; lastError: string | null; instanceCount: number } };
}

const fmt = (s: number) => {
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = Math.floor(s % 60);
  return (h ? `${h}:${String(m).padStart(2, '0')}` : `${m}`) + `:${String(sec).padStart(2, '0')}`;
};

/** "in 1 h 12 min", "in 8 min", or "now" for a timer. */
function until(iso: string | null): string | null {
  if (!iso) return null;
  const m = Math.round((Date.parse(iso) - Date.now()) / 60_000);
  if (m <= 0) return 'now';
  return m < 60 ? `in ${m} min` : `in ${Math.floor(m / 60)} h ${m % 60 ? `${m % 60} min` : ''}`.trim();
}

const RUNNING = new Set(['running', 'recording']);
const CHANGING = new Set(['pending', 'stopping', 'shutting-down', 'creating', 'starting', 'updating', 'deleting']);

/** One line in the live list, the same shape whatever kind of thing it is. */
function LiveRow({ to, name, kind, state, pending, note, right, timer, warn }: {
  to: string; name: string; kind: string; state: string; pending?: string | null; note?: string | null; right?: string | null; timer?: string | null; warn?: boolean;
}) {
  return (
    <Link to={to} className="flex items-center gap-3 px-4 py-2.5 hover:bg-raised/40">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 text-sm font-medium">
          <span className="truncate">{name}</span>
          <span className="shrink-0 text-xs font-normal text-ink-muted">{kind}</span>
        </div>
        {note && <div className={`truncate text-xs ${warn ? 'text-warning' : 'text-ink-muted'}`}>{note}</div>}
      </div>
      {timer !== undefined && (
        <div className={`hidden w-28 text-right text-xs sm:block ${timer ? 'text-ink-secondary' : 'text-warning'}`} title={timer ? 'Stops by itself' : 'No stop timer: it runs until someone stops it'}>
          {timer ? `stops ${timer}` : 'no timer'}
        </div>
      )}
      <StatusPill state={state} pending={pending ?? null} />
      <div className="w-24 text-right text-xs tabular text-ink-secondary">{right ?? ''}</div>
    </Link>
  );
}

export function Overview() {
  const { user, can } = useAuth();
  const [data, setData] = useState<Overview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [posters, setPosters] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const d = await api.get<Overview>('/overview');
        if (cancelled) return;
        setData(d);
        setError(null);
        // Stills for the recent recordings: signed once each, kept while the page is open.
        for (const r of d.recordings?.recent ?? []) {
          if (!r.poster || posters[r.prefix]) continue;
          api.get<{ url: string }>(`/recordings/url?key=${encodeURIComponent(r.poster)}`)
            .then(({ url }) => { if (!cancelled) setPosters((p) => ({ ...p, [r.prefix]: url })); })
            .catch(() => {});
        }
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      }
    };
    void load();
    const timer = setInterval(() => void load(), 20_000);
    return () => { cancelled = true; clearInterval(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!data && !error) return <Spinner label="Looking at everything…" />;
  if (!data) return <Empty title="The overview could not load">{error}</Empty>;

  const { live, costs, attention, people, recordings, activity, hub } = data;
  const running = [
    ...live.machines.filter((m) => RUNNING.has(m.state) || CHANGING.has(m.state)),
    ...live.encoders.filter((e) => RUNNING.has(e.state) || CHANGING.has(e.state) || e.busy),
    ...live.flows.filter((f) => RUNNING.has(f.state) || CHANGING.has(f.state) || f.busy),
    ...live.recorders.filter((r) => r.state !== 'failed'),
  ].length;
  const bad = attention.filter((a) => a.level === 'bad').length;
  const burn = costs?.usdPerHourNow ?? live.machines.reduce((n, m) => n + (m.state === 'running' ? m.usdPerHour ?? 0 : 0), 0);
  const nearestBudget = costs?.budgets.filter((b) => b.thresholdUsd > 0).sort((a, b) => a.spentUsd / a.thresholdUsd > b.spentUsd / b.thresholdUsd ? -1 : 1)[0];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold">{running === 0 ? 'Nothing is running' : `${running} thing${running === 1 ? '' : 's'} running`}</h1>
          <p className="text-sm text-ink-muted">
            {running === 0 ? 'No compute charges are accruing. ' : `Burning ${formatUsd(burn, 2)} an hour. `}
            {bad > 0 ? `${bad} thing${bad === 1 ? ' needs' : 's need'} attention.` : attention.length > 0 ? `${attention.length} worth a look.` : 'All quiet.'}
          </p>
        </div>
        <div className="flex gap-2">
          {can('resource:create') && <Link to="/launch"><Button variant="primary">Launch instance</Button></Link>}
          {can('resource:create') && <Link to="/launch/encoder"><Button variant="secondary">Launch encoder</Button></Link>}
        </div>
      </div>

      {/* Attention: empty on a good day. Red is wrong now; amber is worth a look. */}
      {attention.length > 0 && (
        <Card title="Needs attention">
          <div className="divide-y divide-grid">
            {attention.map((a, i) => {
              const inner = (
                <div className="flex items-start gap-3 px-4 py-2.5 text-sm">
                  <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full" style={{ background: a.level === 'bad' ? 'var(--color-critical)' : 'var(--color-warning)' }} />
                  <span className="min-w-0 flex-1">{a.text}</span>
                  {a.to && <span className="shrink-0 text-xs text-ink-muted">open →</span>}
                </div>
              );
              return a.to ? <Link key={i} to={a.to} className="block hover:bg-raised/40">{inner}</Link> : <div key={i}>{inner}</div>;
            })}
          </div>
        </Card>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile label="Burn rate" value={`${formatUsd(burn, 2)}/hr`} sub={burn > 0 ? `${formatUsd(burn * 24)} a full day` : 'Nothing running'} tone={burn > 5 ? 'warning' : 'default'} />
        {costs ? (
          <StatTile
            label="Month to date"
            value={costs.monthToDate != null ? formatUsd(costs.monthToDate) : formatUsd(costs.monthUsd)}
            sub={nearestBudget ? `${Math.round((nearestBudget.spentUsd / nearestBudget.thresholdUsd) * 100)}% of the "${nearestBudget.name}" budget` : costs.forecast ? `${formatUsd(costs.forecast)} forecast` : 'Whole account'}
            tone={nearestBudget && nearestBudget.spentUsd >= nearestBudget.thresholdUsd ? 'critical' : nearestBudget && nearestBudget.spentUsd / nearestBudget.thresholdUsd > 0.8 ? 'warning' : 'default'}
          />
        ) : (
          <StatTile label="Running" value={running} sub="instances, encoders, flows, recordings" />
        )}
        <StatTile
          label="On the intercom"
          value={people.intercom ? people.intercom.online : '—'}
          sub={people.intercom ? `of ${people.intercom.users} · ${people.intercom.channels} channel${people.intercom.channels === 1 ? '' : 's'}` : people.call ? 'Intercom not answering' : 'Not set up or not yours to see'}
          tone={people.intercom && people.intercom.online > 0 ? 'good' : 'default'}
        />
        <StatTile
          label="Devices and Call"
          value={`${people.devicesOnline}${people.devicesTotal ? `/${people.devicesTotal}` : ''}`}
          sub={people.call ? `${people.call.seatsLive} Call seat${people.call.seatsLive === 1 ? '' : 's'} live in ${people.call.sessionsOpen} session${people.call.sessionsOpen === 1 ? '' : 's'}` : 'devices online'}
          tone={people.devicesOnline > 0 ? 'good' : 'default'}
        />
      </div>

      {/* Everything live, one list. Nothing running is one calm line. */}
      <Card title="Live now" action={<div className="flex gap-1"><Link to="/machines"><Button variant="ghost">Instances</Button></Link><Link to="/encoders"><Button variant="ghost">Encoders</Button></Link></div>}>
        {running === 0 ? (
          <Empty title="Nothing is running">Instances, encoders, flows and recordings appear here while they run.</Empty>
        ) : (
          <div className="divide-y divide-grid">
            {live.recorders.filter((r) => r.state !== 'failed').map((r) => (
              <LiveRow key={r.id} to={r.ownerKind === 'encoder' ? `/encoders/${r.ownerId}` : '/flows'} name={r.name} kind="recording" state={r.state === 'scheduled' ? 'pending' : r.state}
                note={r.state === 'scheduled' && r.startAt ? (Date.parse(r.startAt) <= Date.now() ? 'recording starts when it is running' : `recording starts ${until(r.startAt)}`) : r.startedAt ? `recording for ${formatUptime(r.startedAt)}` : r.state}
                timer={r.state === 'scheduled' ? undefined : until(r.stopAt)} right="" />
            ))}
            {live.encoders.filter((e) => RUNNING.has(e.state) || CHANGING.has(e.state) || e.busy).map((e) => (
              <LiveRow key={e.id} to={`/encoders/${e.id}`} name={e.name} kind="encoder" state={e.busy ? e.state : e.state} pending={e.busy ? e.progress : null}
                note={e.alerts.length ? e.alerts[0].message || e.alerts[0].type : e.activeInput ? `input: ${e.activeInput}` : e.progress}
                warn={e.alerts.length > 0} timer={e.state === 'running' ? until(e.stopAt) : undefined} right={e.usdPerHour != null ? `${formatUsd(e.usdPerHour, 2)}/hr` : ''} />
            ))}
            {live.flows.filter((f) => RUNNING.has(f.state) || CHANGING.has(f.state) || f.busy).map((f) => (
              <LiveRow key={f.id} to="/flows" name={f.name} kind="flow" state={f.state} pending={f.busy ? 'working' : null}
                note={f.encoderId ? 'part of an encoder' : f.error} timer={f.state === 'running' && !f.encoderId ? until(f.stopAt) : undefined}
                right={f.usdPerHour != null ? `${formatUsd(f.usdPerHour, 2)}/hr` : ''} />
            ))}
            {live.machines.filter((m) => RUNNING.has(m.state) || CHANGING.has(m.state)).map((m) => (
              <LiveRow key={m.id} to={`/machines/${m.id}`} name={m.name} kind={m.adopted ? 'adopted instance' : 'instance'} state={m.state} pending={m.pending}
                note={`${m.type ?? ''}${m.groupId ? ` · ${m.groupId}` : ''}${m.since ? ` · up ${formatUptime(m.since)}` : ''}`}
                right={m.state === 'running' && m.sessionUsd != null ? formatUsd(m.sessionUsd) : m.usdPerHour != null ? `${formatUsd(m.usdPerHour, 2)}/hr` : ''} />
            ))}
          </div>
        )}
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        {recordings && (
          <Card title="Latest recordings" action={<Link to="/recordings"><Button variant="ghost">All recordings</Button></Link>}>
            {recordings.recent.length === 0 ? (
              <Empty title="Nothing recorded yet" />
            ) : (
              <div className="divide-y divide-grid">
                {recordings.recent.map((r) => (
                  <Link key={r.prefix} to={`/recordings?open=${encodeURIComponent(`${r.instanceId}/${r.session}`)}`} className="flex items-center gap-3 px-4 py-2 hover:bg-raised/40">
                    <div className="aspect-video w-20 shrink-0 overflow-hidden rounded bg-plane">
                      {posters[r.prefix] && <img src={posters[r.prefix]} alt="" className="h-full w-full object-cover" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">{r.title}</div>
                      <div className="text-xs text-ink-muted">
                        {r.session.replace('T', ' ').replace(/-(\d{2})-(\d{2})$/, ':$1')} · {fmt(r.seconds)}
                        {r.recording && <span style={{ color: 'var(--color-critical)' }}> · ● recording</span>}
                        {r.transcript.state === 'working' && <span className="text-warning"> · transcribing {r.transcript.percent}%</span>}
                        {r.transcript.state === 'ready' && ' · transcript'}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
            {(recordings.joining > 0) && (
              <div className="border-t border-grid px-4 py-2 text-xs text-ink-muted">{recordings.joining} download{recordings.joining === 1 ? '' : 's'} being made on the hub. Do not deploy.</div>
            )}
          </Card>
        )}

        {activity && (
          <Card title="Recent activity" action={user?.isAdmin ? <Link to="/admin"><Button variant="ghost">Audit</Button></Link> : undefined}>
            {activity.length === 0 ? (
              <Empty title="Nothing yet" />
            ) : (
              <div className="divide-y divide-grid">
                {activity.map((a, i) => (
                  <div key={i} className="flex items-start gap-3 px-4 py-2 text-xs">
                    <span className="w-16 shrink-0 text-ink-muted" title={a.at}>{formatRelative(a.at)}</span>
                    <span className="min-w-0 flex-1">
                      <span className="text-ink-secondary">{(a.userEmail ?? 'someone').split('@')[0]}</span>{' '}
                      <span className={a.result === 'success' ? '' : 'text-critical'}>{a.action.replace(/[._]/g, ' ')}</span>
                      {a.targetId && <span className="text-ink-muted"> · {String(a.detail.name ?? a.detail.target ?? a.targetId).slice(0, 48)}</span>}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}
      </div>

      <p className="text-xs text-ink-muted">
        As of {formatRelative(data.at)}. Instance scan {formatRelative(hub.monitor.lastRunAt)}, {hub.monitor.instanceCount} instances.
        {hub.disk && ` Hub disk ${(hub.disk.freeBytes / 1e9).toFixed(0)} GB free of ${(hub.disk.totalBytes / 1e9).toFixed(0)} GB.`}
      </p>
    </div>
  );
}
